import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Download, Check } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp } from "@/components/Reveal";
import {
  Section, H2, H3, P, UL, KPI, InlineCTA,
  useSectorSeo, CTAs, SectorHero, CONTACT_EMAIL,
} from "@/pages/_sectorParts";

const pick = (obj, locale) => (obj ? obj[locale] || obj.fr : "");

/**
 * SectorLayout — Rendu unifié d'une page sectorielle NANDA SÁ.
 * Consomme un objet `data` normalisé (voir /data/sectors/*).
 */
export default function SectorLayout({ data }) {
  const { locale } = useLanguage();

  useSectorSeo({
    tag: `sector-${data.slug}`,
    path: data.path,
    title: pick(data.seo.title, locale),
    description: pick(data.seo.description, locale),
    image: data.hero.image,
    locale,
    breadcrumb: pick(data.hero.breadcrumb, locale),
    keywords: data.seo.keywords,
  });

  return (
    <div data-testid={`page-sector-${data.slug}`}>
      <SectorHero
        heroImg={data.hero.image}
        alt={pick(data.hero.alt, locale)}
        breadcrumb={{
          parent: locale === "en" ? "Sectors" : locale === "pt" ? "Sectores" : "Secteurs",
          current: pick(data.hero.breadcrumb, locale),
        }}
        title={pick(data.hero.title, locale)}
        subtitle={pick(data.hero.subtitle, locale)}
      />

      {/* 1. PRÉSENTATION */}
      <Section testId={`${data.slug}-intro`}>
        <FadeUp>
          <div className="label-overline text-nanda-terracotta mb-6">
            {locale === "en" ? "Introduction" : locale === "pt" ? "Apresentação" : "Présentation"}
          </div>
          <H2>{pick(data.presentation.title, locale)}</H2>
          {pick(data.presentation.paragraphs, locale).map((par, i) => (
            <P key={i}>{par}</P>
          ))}
        </FadeUp>
      </Section>

      {/* 2. CHIFFRES CLÉS */}
      <Section testId={`${data.slug}-chiffres`} className="bg-nanda-bgAlt">
        <FadeUp>
          <div className="label-overline text-nanda-terracotta mb-6">
            {locale === "en" ? "Key figures" : locale === "pt" ? "Dados-chave" : "Chiffres clés"}
          </div>
          <H2>{pick(data.kpis.title, locale)}</H2>
          <div className="grid sm:grid-cols-2 gap-8 md:gap-10 mt-8">
            {data.kpis.items.map((k, i) => (
              <KPI key={i} n={k.n} l={pick(k.l, locale)} d={pick(k.d, locale)} />
            ))}
          </div>
          <p className="text-nanda-muted text-xs mt-8 leading-relaxed">{pick(data.kpis.sources, locale)}</p>
        </FadeUp>
      </Section>

      {/* 3. OPPORTUNITÉS */}
      <Section testId={`${data.slug}-opportunites`}>
        <FadeUp>
          <div className="label-overline text-nanda-terracotta mb-6">
            {locale === "en" ? "Opportunities" : locale === "pt" ? "Oportunidades" : "Opportunités"}
          </div>
          <H2>{pick(data.opportunites.title, locale)}</H2>
          <P>{pick(data.opportunites.intro, locale)}</P>
          {data.opportunites.segments.map((seg, i) => (
            <div key={i}>
              <H3>{pick(seg.h3, locale)}</H3>
              {seg.p ? <P>{pick(seg.p, locale)}</P> : null}
              {seg.items ? <UL items={pick(seg.items, locale)} /> : null}
            </div>
          ))}
        </FadeUp>
        {data.ctas.includes("NandaSa") && <CTAs.NandaSa />}
        {data.ctas.includes("Energia") && <CTAs.Energia />}
        {data.ctas.includes("Eleone") && <CTAs.Eleone />}
      </Section>

      {/* 4. DÉFIS */}
      <Section testId={`${data.slug}-defis`} className="bg-nanda-bgAlt">
        <FadeUp>
          <div className="label-overline text-nanda-terracotta mb-6">
            {locale === "en" ? "Challenges" : locale === "pt" ? "Desafios" : "Défis"}
          </div>
          <H2>{pick(data.defis.title, locale)}</H2>
          <P>{pick(data.defis.intro, locale)}</P>
          <UL items={pick(data.defis.items, locale)} />
          {pick(data.defis.closing, locale) && (
            <P>{pick(data.defis.closing, locale)}</P>
          )}
        </FadeUp>
      </Section>

      {/* 5. POURQUOI NANDA SÁ */}
      <Section testId={`${data.slug}-pourquoi`}>
        <FadeUp>
          <div className="label-overline text-nanda-terracotta mb-6">
            {locale === "en" ? "Why NANDA SÁ" : locale === "pt" ? "Porquê NANDA SÁ" : "Pourquoi NANDA SÁ"}
          </div>
          <H2>{pick(data.pourquoi.title, locale)}</H2>
          {pick(data.pourquoi.paragraphs, locale).map((par, i) => (
            <P key={i}>{par}</P>
          ))}
          {pick(data.pourquoi.bullets, locale) && (
            <UL items={pick(data.pourquoi.bullets, locale)} />
          )}
        </FadeUp>
        {data.ctas.includes("Conseil") && <CTAs.Conseil />}
      </Section>

      {/* 6. TÉLÉCHARGEMENT NOTE PDF */}
      <DownloadForm data={data} locale={locale} />

      {/* 7. CONTACT + CONCLUSION */}
      <Section testId={`${data.slug}-contact`}>
        <FadeUp>
          <div className="label-overline text-nanda-terracotta mb-6">
            {locale === "en" ? "Contact" : locale === "pt" ? "Contacto" : "Contact"}
          </div>
          <H2>{pick(data.contact.title, locale)}</H2>
          <P>{pick(data.contact.body, locale)}</P>
          {pick(data.contact.quote, locale) && (
            <p className="font-serif text-2xl md:text-3xl italic text-nanda-ink mt-8">
              {pick(data.contact.quote, locale)}
            </p>
          )}
          <div className="mt-12 flex flex-wrap gap-4">
            <Link
              to="/opportunites"
              className="btn-arrow inline-flex items-center gap-3 bg-nanda-ink text-white px-7 py-4 hover:bg-nanda-terracotta transition-colors duration-500 label-overline"
            >
              {locale === "en" ? "The full opportunities panorama" : locale === "pt" ? "O panorama completo das oportunidades" : "Le panorama complet des opportunités"}
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
            <Link
              to={`/contact?subject=${encodeURIComponent(pick(data.contact.subject, locale))}`}
              className="btn-arrow inline-flex items-center gap-3 bg-transparent border border-nanda-ink text-nanda-ink px-7 py-4 hover:bg-nanda-ink hover:text-white transition-colors duration-500 label-overline"
            >
              {locale === "en" ? "Write to us —" : locale === "pt" ? "Escreva-nos —" : "Nous écrire —"} {CONTACT_EMAIL}
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </FadeUp>
      </Section>
    </div>
  );
}

/* ────────────── FORMULAIRE TÉLÉCHARGEMENT NOTE PDF ────────────── */
function DownloadForm({ data, locale }) {
  const [form, setForm] = useState({ prenom: "", nom: "", societe: "", pays: "", email: "" });
  const [sent, setSent] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    const titleFr = pick(data.download.mailSubject, locale);
    const subject = encodeURIComponent(titleFr);
    const body = encodeURIComponent(
      `${form.prenom} ${form.nom}\n${form.societe ? form.societe + "\n" : ""}${form.pays}\n${form.email}\n\n` +
        pick(data.download.mailBody, locale)
    );

    // Note PDF dans la langue courante (suffixe -en / -pt, FR sans suffixe)
    const suffix = locale === "en" ? "-en" : locale === "pt" ? "-pt" : "";
    const pdfHref = data.download.pdfUrl.replace(/\.pdf$/, `${suffix}.pdf`);
    const pdfName = data.download.pdfFilename.replace(/\.pdf$/, `${suffix}.pdf`);

    const a = document.createElement("a");
    a.href = pdfHref;
    a.download = pdfName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    setSent(true);
  };

  const L = (fr, en, pt) => (locale === "en" ? en : locale === "pt" ? pt : fr);

  return (
    <Section testId={`${data.slug}-download`} className="bg-nanda-sand/40">
      <FadeUp>
        <div className="label-overline text-nanda-terracotta mb-6">
          {L("Note PDF gratuite", "Free PDF brief", "Nota PDF gratuita")}
        </div>
        <H2>{pick(data.download.title, locale)}</H2>
        <P>{pick(data.download.body, locale)}</P>

        {sent ? (
          <div data-testid={`${data.slug}-download-success`} className="mt-8 border border-nanda-terracotta bg-white p-8 flex items-start gap-4">
            <div className="rounded-full bg-nanda-terracotta text-white p-2"><Check size={18} strokeWidth={2} /></div>
            <div>
              <div className="font-serif text-lg text-nanda-ink mb-2">
                {L("Merci. Votre téléchargement démarre.", "Thank you. Your download is starting.", "Obrigado. O seu download está a iniciar.")}
              </div>
              <p className="text-nanda-inkSoft text-[15px] leading-[1.7]">
                {L("Si le téléchargement ne démarre pas automatiquement,", "If the download does not start automatically,", "Se o download não iniciar automaticamente,")}{" "}
                <a href={data.download.pdfUrl} download className="underline text-nanda-ink hover:text-nanda-terracotta">
                  {L("cliquez ici pour l'obtenir directement", "click here to get it directly", "clique aqui para o obter directamente")}
                </a>.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-10 grid sm:grid-cols-2 gap-5" data-testid={`${data.slug}-download-fields`}>
            <Field name="prenom" label={L("Prénom", "First name", "Nome")} value={form.prenom} onChange={onChange} required />
            <Field name="nom" label={L("Nom", "Last name", "Apelido")} value={form.nom} onChange={onChange} required />
            <Field name="societe" label={L("Société (optionnel)", "Company (optional)", "Empresa (opcional)")} value={form.societe} onChange={onChange} colSpan />
            <Field name="pays" label={L("Pays", "Country", "País")} value={form.pays} onChange={onChange} required />
            <Field name="email" label={L("Adresse e-mail", "Email address", "Endereço de e-mail")} type="email" value={form.email} onChange={onChange} required />
            <div className="sm:col-span-2 pt-4">
              <button
                type="submit"
                data-testid={`${data.slug}-download-submit`}
                className="btn-arrow inline-flex items-center gap-3 bg-nanda-ink text-white px-8 py-4 hover:bg-nanda-terracotta transition-colors duration-500 label-overline"
              >
                <Download size={16} strokeWidth={1.5} />
                {L("Télécharger la note PDF", "Download the PDF brief", "Descarregar a nota PDF")}
              </button>
              <p className="text-nanda-muted text-xs mt-4 max-w-xl leading-relaxed">
                {L(
                  "Vos informations restent confidentielles. Elles ne seront utilisées que pour vous adresser occasionnellement les publications de NANDA SÁ.",
                  "Your information remains confidential. It will only be used to occasionally send you NANDA SÁ publications.",
                  "As suas informações permanecem confidenciais. Serão utilizadas apenas para lhe enviar ocasionalmente as publicações da NANDA SÁ."
                )}
              </p>
            </div>
          </form>
        )}
      </FadeUp>
    </Section>
  );
}

function Field({ name, label, type = "text", value, onChange, required, colSpan }) {
  return (
    <div className={`flex flex-col ${colSpan ? "sm:col-span-2" : ""}`}>
      <label className="label-overline text-nanda-muted mb-2" htmlFor={`f-${name}`}>{label}</label>
      <input
        id={`f-${name}`}
        name={name}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        className="bg-white border border-nanda-line px-4 py-3 text-nanda-ink focus:border-nanda-ink outline-none transition-colors"
      />
    </div>
  );
}
